import {
  Avatar,
  Box,
  Button,
  CountryFlag,
  Flex,
  Input,
  PageGridContainer,
  SimpleGrid,
  TCountryCode,
  Text,
} from "@govlaunch/web";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { forwardRef, useEffect } from "react";
import { connectCurrentRefinements, CurrentRefinementsProvided, InstantSearchProps } from "react-instantsearch-core";
import {
  Configure,
  connectInfiniteHits,
  connectRefinementList,
  connectSearchBox,
  InstantSearch,
} from "react-instantsearch-dom";
import { useIntl, FormattedMessage } from "react-intl";
import { useToggle } from "react-use";
import algoliaClient from "~/components/search/utils/algoliaClient";
import getIndexName from "~/components/search/utils/getIndexName";
import GovernmentsPageSeo from "~/components/seo/GovernmentsPageSeo";
import getCityType from "~/lib/utils/getCityType";
import notUndefinedOrNull from "~/lib/utils/notUndefinedOrNull";
import NavbarLayout from "~/src/components/navbar/NavbarLayout";
import FiltersToggleButton from "~/src/components/home/FiltersToggleButton";
import HomepageNavigation from "~/src/components/home/HomepageNavigation";

const MultiFacetSelect = dynamic(() => import("../MultiFacetSelect"), { ssr: false });

interface IGovernmentsProps extends Omit<InstantSearchProps, "indexName" | "searchClient"> {}

const GovernmentsPage = ({ resultsState, widgetsCollector, ...rest }: IGovernmentsProps) => {
  const [on, toggle] = useToggle(true);

  return (
    <Box>
      <GovernmentsPageSeo />
      <InstantSearch
        indexName={getIndexName("governments")}
        searchClient={algoliaClient}
        resultsState={resultsState}
        widgetsCollector={widgetsCollector}
        {...rest}
      >
        <NavbarLayout>
          <HomepageNavigation />

          <Box display={["flex", "flex", "flex", "none"]} mb={4} justifyContent="center">
            <FiltersToggleButton onClick={toggle} active={on} />
          </Box>

          <PageGridContainer>
            <SimpleGrid
              spacingX={4}
              bg={["none", "none", "none", "blue.100"]}
              px={[0, 0, 0, 10]}
              py={[0, 0, 3]}
              display="flex"
              alignItems="center"
              flexDirection={["column", "column", "column", "row"]}
            >
              <Box display={["none", "none", "none", "flex"]} alignItems="center">
                <svg width="17" height="10" viewBox="0 0 17 10" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.5 2H0.5V0H16.5V2ZM14.0999 6H2.8999V4H14.0999V6ZM5.2998 10H11.6998V8H5.2998V10Z"
                    fill="#3F4A5A"
                  />
                </svg>
                <Text as="span" fontSize="md" ml={3} fontWeight="semibold" color="#3F4A5A">
                  <FormattedMessage defaultMessage="Filter by" id="S57QRB" />
                </Text>
              </Box>
              {on && (
                <>
                  <Box mt={[2, 2, 2, 0]} flex={1} width={["100%", "100%", "100%", "auto"]}>
                    <SearchBox />
                  </Box>
                  <ClearStatesWithCountries />

                  <Box mt={[2, 2, 2, 0]} width={["100%", "100%", "100%", "auto"]}>
                    <CountriesFilter attribute="country" limit={100} />
                  </Box>
                  <Box mt={[2, 2, 2, 0]} width={["100%", "100%", "100%", "auto"]}>
                    <StatesFilter attribute="city.stateName" limit={500} />
                  </Box>
                </>
              )}
            </SimpleGrid>
            <GovernmentResults />
          </PageGridContainer>
        </NavbarLayout>
      </InstantSearch>
    </Box>
  );
};

const ClearStatesWithCountries = connectCurrentRefinements<CurrentRefinementsProvided>(({ ...props }) => {
  const countries = props.items.find((item) => item.attribute === "country");

  useEffect(() => {
    if (!countries?.currentRefinement) {
      props.refine((searchState) => {
        return {
          ...searchState,
          refinementList: {
            "city.stateName": [],
          },
        };
      });
    }
  }, [countries]);
  return null;
});

const CountriesFilter = connectRefinementList(({ refine, items, currentRefinement }) => {
  const intl = useIntl();

  return (
    <MultiFacetSelect
      selectedItems={currentRefinement}
      onChange={(countries) => {
        refine(countries);
      }}
      ariaLabel="Filter by Country"
      placeholder={
        currentRefinement.length > 0
          ? currentRefinement.join(", ")
          : intl.formatMessage({ defaultMessage: "Country", id: "vONi+O" })
      }
      items={items
        .map((item) => ({
          label: item.label,
          value: item.label,
        }))
        .sort((a, b) => {
          return a.label.localeCompare(b.label);
        })}
    />
  );
});

const StatesFilter = connectRefinementList(({ refine, items, currentRefinement }) => {
  const intl = useIntl();

  return (
    <MultiFacetSelect
      selectedItems={currentRefinement}
      onChange={(countries) => {
        refine(countries);
      }}
      ariaLabel="Filter by State/Province/Territory"
      placeholder={
        currentRefinement.length > 0
          ? currentRefinement.join(", ")
          : intl.formatMessage({
              defaultMessage: "State/Province/Territory",
              id: "SI/UQT",
            })
      }
      items={items
        .map((item) => {
          return {
            label: item.label,
            value: item.label,
          };
        })
        .filter(notUndefinedOrNull)
        .sort((a, b) => {
          return a.label.localeCompare(b.label);
        })}
    />
  );
});

const SearchBox = connectSearchBox(({ refine, currentRefinement }) => {
  const intl = useIntl();

  return (
    <Input
      placeholder={intl.formatMessage({
        defaultMessage: "Search for governments...",
        id: "sOHiob",
      })}
      value={currentRefinement}
      onChange={(e) => refine(e.target.value)}
      width="100%"
    />
  );
});

const GovernmentResults = connectInfiniteHits(({ hits, hasMore, refineNext }) => {
  const intl = useIntl();

  return (
    <>
      <Configure hitsPerPage={30} />
      <SimpleGrid columns={[1, 2, 3]} spacing={6} mt={6}>
        {hits.map((government) => (
          <Link
            key={government.slug}
            href={`/government?governmentSlug=${government.slug}`}
            as={`/governments/${government.slug}`}
            passHref={true}
          >
            <GovernmentWithTypeAndFlag government={government} />
          </Link>
        ))}
      </SimpleGrid>
      {hasMore && (
        <Box display="flex" justifyContent="center" py={6}>
          <Button
            variant="secondary"
            size="sm"
            rightIcon={(props) => (
              <svg width="24" height="12" viewBox="0 0 24 12" fill="none" {...props}>
                <path
                  d="M2 2.5L11.7436 9.55882L22 2.5"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            onClick={refineNext}
          >
            {intl.formatMessage({
              defaultMessage: "Show more",
              id: "aWpBzj",
            })}
          </Button>
        </Box>
      )}
    </>
  );
});

export default GovernmentsPage;

interface IGovernmentHit {
  _id: string;
  objectID: string;
  name: string;
  city: {
    governmentType: string;
    country: string;
  };
  country: string;
  logo: string;
}

const GovernmentWithTypeAndFlag = forwardRef<any, { government: IGovernmentHit }>(({ government, ...rest }, ref) => {
  return (
    <Flex align="center" overflow="hidden" as="a" ref={ref} {...rest}>
      <Avatar size="sm" name={government.name} src={government.logo} />
      <Box isTruncated={true}>
        <Box ml={3} isTruncated={true}>
          <Text fontSize="md" color="gray.600" isTruncated={true}>
            {government.name}
          </Text>
        </Box>
        <Box display="inline-flex" alignItems="center" ml={3} flex={1}>
          <CountryFlag width={24} height={14} countryCode={government.city.country as TCountryCode} />
          <Text fontSize="sm" color="blue.600" ml={2} mb={0}>
            {getCityType(government.city.governmentType)} - {government.country}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
});

GovernmentWithTypeAndFlag.displayName = "GovernmentWithTypeAndFlag";
